<template>
  <div
    class="play-input-group"
    :class="{ 'is-disabled': disabled }"
  >
    <!-- leading addon: `prepend` prop (plain text) or #prepend slot -->
    <span
      v-if="prepend || $slots.prepend"
      class="play-input-group__addon play-input-group__addon--prepend"
    >
      <slot name="prepend">{{ prepend }}</slot>
    </span>

    <div class="play-input-group__field">
      <slot />
    </div>

    <!-- trailing addon: `append` prop (plain text) or #append slot -->
    <span
      v-if="append || $slots.append"
      class="play-input-group__addon play-input-group__addon--append"
    >
      <slot name="append">{{ append }}</slot>
    </span>
  </div>
</template>

<script>
/**
 * PlayInputGroup — a single soft-fill control box with optional leading /
 * trailing addons, wrapping a field. Replaces bootstrap-vue's
 * <b-input-group> (+ -prepend / -append / -text). The nested input is
 * flattened (border/background stripped) so the group reads as one control.
 *
 * Use the `prepend` / `append` string props for text addons, or the
 * `#prepend` / `#append` slots for custom content (icons, buttons). For a
 * simple fixed prefix/suffix on a lone field, prefer PlayInput's
 * `#prefix` / `#suffix` slots.
 */
export default {
  name: 'PlayInputGroup',
  props: {
    prepend: {
      type: String,
      default: '',
    },
    append: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
.play-input-group {
  display: flex;
  align-items: stretch;
  background: var(--surface-sunk);
  border: var(--border-w) solid transparent;
  border-radius: var(--radius);
  transition:
    background var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out);

  &:focus-within {
    background: var(--surface-card);
    border-color: var(--c-secondary);
    box-shadow: 0 0 0 3px var(--c-secondary-soft);
  }

  &__field {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: stretch;
  }

  /* flatten whatever field sits inside so the group is one seamless control */
  ::v-deep .play-input,
  ::v-deep .play-input__control,
  ::v-deep input,
  ::v-deep select,
  ::v-deep textarea,
  ::v-deep .play-select__control {
    flex: 1;
    min-width: 0;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
  }
  ::v-deep .play-input { display: block; }
  ::v-deep .play-input__field { padding: 12px 15px; }

  &__addon {
    display: inline-flex;
    align-items: center;
    flex: none;
    padding: 0 14px;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 14px;
    color: var(--text-muted);
    white-space: nowrap;
  }
  &__addon--prepend { padding-inline-end: 6px; }
  &__addon--append { padding-inline-start: 6px; }

  &.is-disabled { opacity: .55; pointer-events: none; }
}
</style>
